import { ABNORMAL_COLORS } from '@quickreport/domain';
import { type AbnormalLook, AbnormalPicker, Button, describeLook, Modal } from '@quickreport/ui';
import { useState } from 'react';

export interface StyleChoice {
  /** One result, or every result in this report. */
  scope: 'result' | 'report';
  /** The look to use, or null to go back to the setting above it. */
  look: AbnormalLook | null;
}

/**
 * How a result prints when it is outside the normal range, for this one result or for the whole
 * report. The lab's own setting is the starting point; this is the way to differ from it for a
 * patient who does not want red, or to mark a value as a warning.
 */
export function ResultStyleDialog({
  label,
  lab,
  inherited,
  inheritedFrom,
  own,
  onSave,
  onClose,
}: {
  label: string;
  /** What the lab's setting gives. */
  lab: AbnormalLook;
  /** What this result gets when it has no choice of its own (the report's, else the lab's). */
  inherited: AbnormalLook;
  inheritedFrom: 'report' | 'lab' | 'default';
  /** This result's own choice, if it has one. */
  own: AbnormalLook | null;
  onSave: (c: StyleChoice) => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<'inherit' | 'custom'>(own ? 'custom' : 'inherit');
  const [look, setLook] = useState<AbnormalLook>(own ?? inherited);
  const [wholeReport, setWholeReport] = useState(false);

  const inheritWords = wholeReport
    ? `Back to the lab’s setting (${describeLook(lab)})`
    : inheritedFrom === 'report'
      ? `Same as the rest of this report (${describeLook(inherited)})`
      : `Same as the lab’s setting (${describeLook(inherited)})`;

  return (
    <Modal title="How this result prints" label="How this result prints" onClose={onClose}>
      <div className="grid gap-4">
        <p className="text-[13px] text-slate">
          When <b className="text-ink">{label}</b> is outside the normal range, print it like this.
          Normal values always print in plain black.
        </p>

        <div className="grid gap-2" role="radiogroup" aria-label="Choose">
          <Choice
            checked={mode === 'inherit'}
            onSelect={() => setMode('inherit')}
            title={inheritWords}
          />
          <Choice
            checked={mode === 'custom'}
            onSelect={() => setMode('custom')}
            title={wholeReport ? 'Choose a look for this report' : 'Choose for this result'}
          />
        </div>

        {mode === 'custom' ? (
          <AbnormalPicker value={look} onChange={setLook} />
        ) : (
          <div className="flex items-center gap-2 rounded-lg bg-canvas px-3 py-2 text-[13px]">
            <span
              className="h-3.5 w-3.5 rounded-full ring-1 ring-black/10"
              style={{ background: ABNORMAL_COLORS[(wholeReport ? lab : inherited).color].hex }}
            />
            <span>
              Out-of-range values print <b>{describeLook(wholeReport ? lab : inherited)}</b>.
            </span>
          </div>
        )}

        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-line px-3 py-2.5">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-brand"
            checked={wholeReport}
            onChange={(e) => setWholeReport(e.target.checked)}
          />
          <span>
            <span className="block font-medium">Use this for every result in this report</span>
            <span className="block text-[12px] text-slate">
              Only this report changes. The lab’s setting and other reports stay as they are.
            </span>
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            tone="primary"
            onClick={() =>
              onSave({
                scope: wholeReport ? 'report' : 'result',
                look: mode === 'custom' ? look : null,
              })
            }
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Choice({
  checked,
  onSelect,
  title,
}: {
  checked: boolean;
  onSelect: () => void;
  title: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onSelect}
      className={`flex min-h-11 items-center gap-3 rounded-lg border px-3 py-2 text-left text-[14px] ${
        checked ? 'border-brand bg-brand-soft' : 'border-line bg-paper hover:bg-canvas'
      }`}
    >
      <span
        className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border-2 ${checked ? 'border-brand' : 'border-slate/50'}`}
      >
        {checked ? <span className="h-2 w-2 rounded-full bg-brand" /> : null}
      </span>
      <span className="font-medium">{title}</span>
    </button>
  );
}
